'use client';
import { useTranslations } from 'next-intl';
import { type FC, type ReactNode, useMemo } from 'react';
import { BaseLineDelete } from '@/app/components/icons/BaseLineDelete';
import { BaseLineExtend } from '@/app/components/icons/BaseLineExtend';
import { BaseLineExtractentrance } from '@/app/components/icons/BaseLineExtractentrance';
import { BaseLineFeedback } from '@/app/components/icons/BaseLineFeedback';
import { BaseLineQuickEdit } from '@/app/components/icons/BaseLineQuickEdit';
import { BaseLineShare2 } from '@/app/components/icons/BaseLineShare2';
import { BaseLineSubmit3 } from '@/app/components/icons/BaseLineSubmit3';
import { BaseLineUpscale } from '@/app/components/icons/BaseLineUpscale';
import { ProductLineTransform } from '@/app/components/icons/ProductLineTransform';
import { getExtensionToolPath, getQuickEditToolPath } from '@/app/components/shared/CreationDetail/utils';
import { CustomTooltip } from '@/app/components/shared/CustomTooltip';
import { DownloadButton, type WatermarkMode } from '@/app/components/shared/DownloadButton';
import { EllipsisDropdownMenu } from '@/app/components/shared/EllipsisDropdownMenu';
import { NewFeatureTooltip } from '@/app/components/shared/NewFeatureTooltip';
import { StarButton } from '@/app/components/shared/StarButton';
import { Button } from '@/app/components/ui/button';
import { NewFeatureKeys, Q2_PRO_REFERENCE_VIDEO_MAX_DURATION, SITE } from '@/app/constants';
import {
  useCheckEnableCreateElement,
  useGlobalModal,
  useIsScreen,
  useNewFeature,
  useUpscaleNavigation,
} from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import { type CreationInfo, EllipsisActionTypeEnum, type ImagePrompt, type TaskInfo, type VoteType } from '@/app/types';
import {
  buildExtensionToolDraftFromCreation,
  buildQuickEditToolDraftFromCreation,
  cn,
  generateFileName,
  getReportParams,
  isVideoDurationWithinRange,
  reportEvent,
} from '@/app/utils';
import { useRouter } from '@/i18n/routing';
import { ActionButtonType } from '../types';

export interface CreationActionButtonsProps {
  // 基础数据
  creation: CreationInfo | undefined;
  task: TaskInfo;

  /** 要显示的操作按钮类型列表 */
  showActionList: ActionButtonType[];

  // 特殊回调（需要外部处理的逻辑）
  onSubmit?: (creation: CreationInfo) => void;
  onSelectedAction: (action: EllipsisActionTypeEnum) => void;
  onDropdownOpenChange?: (open: boolean) => void;
}

export const CreationActionButtons: FC<CreationActionButtonsProps> = ({
  creation,
  task,
  showActionList,
  onSubmit,
  onSelectedAction,
  onDropdownOpenChange,
}) => {
  const t = useTranslations();
  const router = useRouter();
  const { isSmScreen, isMdScreen, isLgScreen } = useIsScreen();
  const { navigateToUpscale } = useUpscaleNavigation();

  // 从全局 store 获取数据和方法
  const {
    updateCreation,
    userInfo,
    isFirstSuccessVideoTask,
    openReferenceModalWithData,
    replaceExtensionToolDraft,
    replaceQuickEditToolDraft,
    isTeamSpace,
  } = useStore((state) => ({
    updateCreation: state.updateCreation,
    userInfo: state.userInfo,
    isFirstSuccessVideoTask: state.isFirstSuccessVideoTask,
    openReferenceModalWithData: state.openReferenceModalWithData,
    replaceExtensionToolDraft: state.replaceExtensionToolDraft,
    replaceQuickEditToolDraft: state.replaceQuickEditToolDraft,
    isTeamSpace: state.isTeamSpace,
  }));

  const isFree = userInfo?.subs_plan === 'free';

  // 获取全局 modal 方法
  const { open: openShareDialog } = useGlobalModal('shareDialog');
  const { open: openFrameShotDialog } = useGlobalModal('frameShotDialog');

  const taskHistoryContainer = document.getElementById('taskHistoryContainer');

  // 获取检查是否可以创建主体的方法
  const { checkCreateElementEnablePromise } = useCheckEnableCreateElement();

  // 获取新功能相关方法
  const { isNewFeature, markAsClicked } = useNewFeature();
  const { type, settings, id: taskId } = task;
  const creationId = creation?.id || '';
  const isStarred = creation?.is_favor || false;
  const vote = creation?.vote;
  const isAudioTask = type === 'text2audio' || type === 'tts';

  const isQuickEditNew = useMemo(() => {
    // 视频任务：首个成功的视频任务且时长 <= Q2 Pro 参考生视频最大时长
    if (
      creation?.type === 'video' &&
      creation.duration !== undefined &&
      isVideoDurationWithinRange({
        duration: creation.duration,
        maxDuration: Q2_PRO_REFERENCE_VIDEO_MAX_DURATION,
      }) &&
      isFirstSuccessVideoTask(taskId)
    ) {
      return Boolean(isNewFeature(NewFeatureKeys.QUICK_EDIT));
    }
    return false;
  }, [taskId, creation?.type, creation?.duration, isNewFeature, isFirstSuccessVideoTask]);

  // 生成报告参数
  const reportParams = { ...getReportParams(type, settings, task.scene), interaction_position: `${type}_task_card` };

  const handleSubmit = () => {
    if (creation && onSubmit) {
      onSubmit(creation);
    }
  };

  const handleUpscale = () => {
    navigateToUpscale(creation, {
      taskId,
      reportParams,
    });
  };

  const handleFrameShot = () => {
    if (!creation) return;

    const videoDom = document.querySelector(`#creation-${creation.id} video`) as HTMLVideoElement | null;

    openFrameShotDialog({
      videoDom: videoDom,
      creation,
      videoCurrentTime: videoDom?.currentTime || 0,
      currentTask: task,
      containerDiv: taskHistoryContainer,
      showFlyTrace: true,
      openInNewTab: false,
    });

    reportEvent('extraction_click');
  };

  const handleShare = async () => {
    if (!creation) return;

    openShareDialog({
      creationId,
      creationType: creation.type as 'video' | 'image' | 'audio',
      taskId,
      reportParams,
    });
  };

  const handleVote = (vote?: VoteType) => {
    updateCreation(taskId, creationId, {
      vote: vote ?? 'unspecified',
    });
  };

  const handleStar = (isStarred: boolean) => {
    updateCreation(taskId, creationId, {
      is_favor: isStarred,
    });
  };

  const handleExtend = () => {
    if (!creation) return;
    replaceExtensionToolDraft(buildExtensionToolDraftFromCreation(creation));
    router.push(getExtensionToolPath());
  };

  const handleQuickEdit = async () => {
    if (!creation) return;

    // 图片类型
    if (creation.type === 'image') {
      replaceQuickEditToolDraft(
        'image',
        buildQuickEditToolDraftFromCreation({
          mode: 'image',
          creation,
          originalMediaName: t('create.createForm.originalImageName'),
          userPlan: userInfo?.subs_plan,
        }),
      );
      router.push(getQuickEditToolPath('image'));
      return;
    }

    if (creation.type === 'video') {
      // 标记为新功能已点击
      markAsClicked(NewFeatureKeys.QUICK_EDIT);

      replaceQuickEditToolDraft(
        'video',
        buildQuickEditToolDraftFromCreation({
          mode: 'video',
          creation,
          originalMediaName: t('create.createForm.originalVideoName'),
          userPlan: userInfo?.subs_plan,
          defaultAspectRatio: settings?.aspect_ratio,
        }),
      );
      router.push(getQuickEditToolPath('video'));
    }
  };

  const handleTransformToElement = () => {
    if (!creation || creation.type !== 'image') return;

    // 优先使用无水印图片
    const imageUrl = creation.nomark_uri || creation.uri;
    if (!imageUrl) return;

    // 创建 ImagePrompt 对象
    const imagePrompt: ImagePrompt = {
      sourceImage: imageUrl,
      sourceImagePreview: imageUrl,
      selectedImage: imageUrl,
      selectedImagePreview: imageUrl,
    };

    // 检查是否可以创建主体
    checkCreateElementEnablePromise({ query: {} }).then(({ error }) => {
      if (error) {
        return;
      }

      // 打开主体创建弹窗
      openReferenceModalWithData({
        referenceModalOpen: true,
        element: { modality: 'image', optionalAssets: [imagePrompt] },
        referenceModalMode: 'create',
        referenceModalType: 'reference',
        referenceModalFrom: 'others',
      });
    });
  };

  const ellipsisDropdownMenuActions = useMemo(() => {
    const actions: Array<
      | { action: EllipsisActionTypeEnum; icon: ReactNode }
      | { key: string; label: string; icon: ReactNode; onClick: () => void }
    > = [
      isAudioTask
        ? { action: EllipsisActionTypeEnum.T2AFeedback, icon: <BaseLineFeedback /> }
        : { action: EllipsisActionTypeEnum.Report, icon: <BaseLineFeedback /> },
      { action: EllipsisActionTypeEnum.Delete, icon: <BaseLineDelete /> },
    ];

    if (isLgScreen && showActionList.includes(ActionButtonType.UPSCALE) && settings?.resolution !== '4k' && creation) {
      actions.push({
        key: ActionButtonType.UPSCALE,
        label: t('task.action.upscale'),
        icon: <BaseLineUpscale />,
        onClick: handleUpscale,
      });
    }

    if (isLgScreen && showActionList.includes(ActionButtonType.SHARE) && creation) {
      actions.push({
        key: ActionButtonType.SHARE,
        label: t('task.action.share'),
        icon: <BaseLineShare2 />,
        onClick: handleShare,
      });
    }

    if (isSmScreen && showActionList.includes(ActionButtonType.TRANSFORM_TO_ELEMENT) && creation?.type === 'image') {
      actions.push({
        key: ActionButtonType.TRANSFORM_TO_ELEMENT,
        label: t('create.char2VideoForm.element.addToMyRef'),
        icon: <ProductLineTransform />,
        onClick: handleTransformToElement,
      });
    }

    return actions;
  }, [creation, isLgScreen, isSmScreen, settings?.resolution, showActionList, t, type]);

  // 渲染函数映射
  const renderActionButton = (actionType: ActionButtonType) => {
    switch (actionType) {
      case ActionButtonType.QUICK_EDIT:
        if (!creation) return null;
        // 图片类型
        if (creation.type === 'image') {
          return (
            <CustomTooltip
              key="quickEdit"
              trigger="hover"
              content={t('task.action.quickEdit')}
              container={taskHistoryContainer}
            >
              <Button
                variant="text"
                size="original"
                className="rounded-8 p-1.5 text-inherit text-xl hover:bg-system-hover01"
                onClick={handleQuickEdit}
              >
                <BaseLineQuickEdit className="text-system-white" />
              </Button>
            </CustomTooltip>
          );
        }
        // 视频类型
        if (creation.type === 'video') {
          return (
            <NewFeatureTooltip
              key="quickEdit"
              isNew={isQuickEditNew}
              newText={t('task.action.quickEditNew')}
              normalText={t('task.action.quickEdit')}
              container={taskHistoryContainer}
            >
              <Button
                variant="text"
                size="original"
                className={cn(
                  'rounded-8 p-1.5 text-inherit text-xl hover:bg-system-hover01',
                  isQuickEditNew &&
                    'relative before:absolute before:inset-0 before:bg-system-blue02 before:shadow-[inset_0_0_10px_#0086FF,inset_0_0_2.5px_#0086FF] before:blur-[7px] before:drop-shadow-[0_0_2.5px_rgba(0,134,255,0.6)]',
                )}
                onClick={handleQuickEdit}
              >
                <BaseLineQuickEdit className={cn('text-system-white', isQuickEditNew && 'z-10')} />
              </Button>
            </NewFeatureTooltip>
          );
        }
        return null;

      case ActionButtonType.TRANSFORM_TO_ELEMENT:
        if (!creation || creation.type !== 'image' || isSmScreen) return null;
        return (
          <CustomTooltip
            key="transformToElement"
            trigger="hover"
            content={t('create.char2VideoForm.element.addToMyRef')}
            container={taskHistoryContainer}
          >
            <Button
              variant="text"
              size="original"
              className="rounded-8 p-1.5 text-inherit text-xl hover:bg-system-hover01"
              onClick={handleTransformToElement}
            >
              <ProductLineTransform className="text-system-white" />
            </Button>
          </CustomTooltip>
        );

      case ActionButtonType.SUBMIT:
        if (!creation || isTeamSpace) return null;
        return (
          <CustomTooltip
            key="submit"
            content={creation.is_asset ? t('task.action.submitted') : t('task.action.submit')}
            container={taskHistoryContainer}
          >
            <Button
              variant="text"
              size="original"
              className="rounded-8 p-1.5 text-inherit text-xl hover:bg-system-hover01"
              disabled={creation.is_asset}
              onClick={handleSubmit}
            >
              <BaseLineSubmit3
                className={cn('text-system-white', creation.is_asset && 'text-system-white opacity-[.40]')}
              />
            </Button>
          </CustomTooltip>
        );

      case ActionButtonType.DOWNLOAD: {
        if (!creation) return null;
        let watermarkMode: WatermarkMode = 'both';
        if (SITE === 'china' && (isAudioTask || isFree)) {
          // 国内站 & (音频 / 免费用户) => 带水印
          watermarkMode = 'watermarkOnly';
        } else if (SITE === 'global' && isAudioTask) {
          // 海外站 & 音频 => 只有不带水印的
          watermarkMode = 'noWatermarkOnly';
        }
        return (
          <DownloadButton
            key="download"
            creationType={creation.type}
            className="rounded-8 p-1.5 text-inherit text-xl hover:bg-system-hover01"
            waterMarkUri={creation.uri || ''}
            noWaterMarkUri={creation.nomark_uri || ''}
            audioUri={isAudioTask ? creation.uri : undefined}
            downloadFileName={generateFileName({ type: creation.type || 'video', id: creation.id || '' })}
            watermarkMode={watermarkMode}
            taskId={taskId}
            creationId={creationId}
            reportParams={reportParams}
            container={taskHistoryContainer}
            hasCopyright={creation.has_copyright}
          />
        );
      }

      case ActionButtonType.UPSCALE:
        // 只有非4K时才显示超分按钮
        if (settings?.resolution === '4k' || !creation) return null;
        return (
          <CustomTooltip
            key="upscale"
            trigger="hover"
            content={t('task.action.upscale')}
            container={taskHistoryContainer}
          >
            <Button
              className="flex aspect-square h-full cursor-pointer items-center justify-center rounded-8 bg-transparent p-1.5 text-lg hover:bg-system-hover01 lg:hidden"
              onClick={handleUpscale}
            >
              <BaseLineUpscale />
            </Button>
          </CustomTooltip>
        );

      case ActionButtonType.FRAME_SHOT:
        // 中等屏幕及以下不显示截帧按钮
        if (isMdScreen || !creation) return null;
        return (
          <CustomTooltip
            key="frameShot"
            trigger="hover"
            content={t('task.action.frameExtraction')}
            container={taskHistoryContainer}
          >
            <Button
              className="flex aspect-square h-full cursor-pointer items-center justify-center rounded-8 bg-transparent p-1.5 text-lg hover:bg-system-hover01"
              onClick={handleFrameShot}
            >
              <BaseLineExtractentrance />
            </Button>
          </CustomTooltip>
        );

      case ActionButtonType.SHARE:
        if (!creation) return null;
        return (
          <CustomTooltip key="share" trigger="hover" content={t('task.action.share')} container={taskHistoryContainer}>
            <div
              className="skip-login-check flex aspect-square h-full cursor-pointer items-center justify-center rounded-8 bg-transparent p-1.5 text-lg hover:bg-system-hover01 lg:hidden"
              onClick={handleShare}
            >
              <BaseLineShare2 />
            </div>
          </CustomTooltip>
        );

      case ActionButtonType.ELLIPSIS_MENU:
        return (
          <EllipsisDropdownMenu
            key="ellipsisMenu"
            side="top"
            className="rounded-8 p-1.5 text-inherit text-xl hover:bg-system-hover01 data-[state=open]:bg-system-hover01"
            actions={ellipsisDropdownMenuActions}
            // 如果生成物有错误，则没有点赞按钮
            creationId={creation?.err_info?.err_code ? '' : creationId}
            creationType={creation?.type}
            taskId={taskId}
            currentVote={vote}
            onVote={handleVote}
            reportParams={reportParams}
            onSelectedAction={onSelectedAction}
            onDropdownOpenChange={onDropdownOpenChange}
          />
        );

      case ActionButtonType.STAR:
        if (!creation) return null;
        return (
          <CustomTooltip key="star" trigger="hover" content={t('task.action.mark')} container={taskHistoryContainer}>
            <div className="aspect-square h-full rounded-8">
              <StarButton
                className="p-1.5 text-inherit text-xl hover:bg-system-hover01 aria-selected:text-system-white"
                id={creationId}
                isStarred={isStarred}
                onStar={handleStar}
                reportParams={reportParams}
              />
            </div>
          </CustomTooltip>
        );
      case ActionButtonType.EXTEND: {
        const resolutionNorForExtend = creation?.resolution?.resolution !== '1080p';
        return (
          <CustomTooltip
            key="extend"
            trigger="hover"
            content={resolutionNorForExtend ? t('task.extendOnly1080p') : t('task.action.extend')}
            container={taskHistoryContainer}
          >
            <Button
              className="flex aspect-square h-full cursor-pointer items-center justify-center rounded-8 bg-transparent p-1.5 text-lg hover:bg-system-hover01"
              disabled={resolutionNorForExtend}
              onClick={handleExtend}
            >
              <BaseLineExtend className="text-system-white" />
            </Button>
          </CustomTooltip>
        );
      }
      default:
        return null;
    }
  };

  if (showActionList.length === 0) {
    return null;
  }

  return <>{showActionList.map(renderActionButton).filter(Boolean)}</>;
};
